/**
 * Что делать с пришедшим по сокету кадром MESSAGE: звук, счётчик непрочитанных, баннер.
 *
 * <p><b>Зачем отдельным модулем.</b> Правило жило внутри `ws.onmessage` и там же незаметно
 * ошибалось. Сервер переиздаёт то же сообщение, когда кто-то поставил реакцию или галочку в
 * чеклисте, и `senderId` в таком кадре — автор исходного сообщения, а не тот, кто нажал.
 * Обработчик читал кадр как входящее, и получалось наоборот: у всех, кроме автора, играл
 * звук и рос счётчик на чужой смайлик, а сам автор — тот, кому галочка в его чеклисте и
 * адресована, — не получал ничего, потому что сообщение считалось «своим». Теперь такие
 * кадры помечены `update`, и правило вынесено сюда, где его видно целиком.
 */
type IncomingMessageEffects = {
  /** Проиграть звук темы. */
  sound: boolean;
  /** Дёрнуть подписчиков «пришло сообщение» — по ним растёт бейдж в списке чатов. */
  bumpUnread: boolean;
  /** Показать локальный баннер (foreground; в фоне то же самое рисует серверный пуш). */
  banner: boolean;
};

type IncomingMessageInput = {
  /** Кадр — про моё же сообщение. */
  mine: boolean;
  /** Кадр — правка (реакция, галочка, изменённый анонс), а не новое сообщение. */
  isUpdate: boolean;
  /**
   * Служебное сообщение о звонке: его рисует слой звонков, а не чат.
   *
   * <p>Ленивая нарочно: чтобы это узнать, надо разобрать шифротекст, а на своё же сообщение
   * и на правку ответ всё равно «ничего не делаем». Обработчик сокета — горячий путь, и
   * лишний разбор на каждом эхе собственной отправки там не нужен.
   */
  isCallEvent: () => boolean;
  /** Чат заглушён пользователем. */
  muted: boolean;
  /** Этот чат сейчас открыт на экране. */
  viewingThisChat: boolean;
  /** Приложение на переднем плане. */
  appActive: boolean;
};

const SILENT: IncomingMessageEffects = { sound: false, bumpUnread: false, banner: false };

export function incomingMessageEffects(input: IncomingMessageInput): IncomingMessageEffects {
  const announce = !input.mine && !input.isUpdate;
  if (!announce || input.isCallEvent()) {
    return SILENT;
  }
  // Звук играем и в открытом чате — иначе входящее в него приходило беззвучно.
  const sound = !input.muted;
  // А вот бейдж и баннер в открытом чате не нужны: человек и так смотрит на сообщение.
  const bumpUnread = !input.viewingThisChat;
  // В фоне локальный баннер не рисуем: там за него отвечает серверный пуш, и два пути
  // дрались за один и тот же дедуп-слот — WS «забирал» его, push глушился, а баннера не
  // было видно вообще.
  const banner = bumpUnread && !input.muted && input.appActive;
  return { sound, bumpUnread, banner };
}
